import { motion } from 'framer-motion';
import {
    ArrowRight,
    CheckCircle,
    QrCode,
    Shield,
    Wallet,
    Zap
} from 'lucide-react';
import { Link } from 'react-router-dom';

const DocumintWallet = () => {
    return (
        <div className="min-h-screen pt-20">
            {/* Hero Section */}
            <section className="py-20 bg-surface-100">
                <div className="container-custom">
                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        <motion.div
                            initial={{ opacity: 0, x: -50 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.6 }}
                        >
                            <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-green-500/20 to-emerald-500/20 rounded-full mb-6 border border-green-500/30">
                                <span className="text-green-400 font-semibold text-sm">🎉 100% FREE</span>
                                <span className="text-gray-300 text-sm ml-2">• No Hidden Fees • Lifetime Access</span>
                            </div>
                            
                            <h1 className="text-4xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                                Your Digital 
                                <span className="bg-gradient-to-r from-neon-300 via-primary-400 to-neon-300 bg-clip-text text-transparent"> Credential Wallet</span>
                            </h1>
                            
                            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                                Experience the future of identity management with your <strong className="text-white">personal digital wallet</strong>. 
                                Receive, store, and share your verifiable credentials with unprecedented security and complete privacy control.
                            </p>

                            <div className="grid md:grid-cols-2 gap-6 mb-8">
                                <div className="flex items-start space-x-3">
                                    <div className="w-8 h-8 bg-green-500/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                                        <CheckCircle className="w-5 h-5 text-green-400" />
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-white mb-1">Free Forever</h3>
                                        <p className="text-sm text-gray-300">Download and use without any costs</p>
                                    </div>
                                </div>
                                <div className="flex items-start space-x-3">
                                    <div className="w-8 h-8 bg-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                                        <Shield className="w-5 h-5 text-blue-400" />
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-white mb-1">Bank-Level Security</h3>
                                        <p className="text-sm text-gray-300">Military-grade encryption protection</p>
                                    </div>
                                </div>
                                <div className="flex items-start space-x-3">
                                    <div className="w-8 h-8 bg-purple-500/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                                        <Wallet className="w-5 h-5 text-purple-400" />
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-white mb-1">Your Data, Your Control</h3>
                                        <p className="text-sm text-gray-300">Complete ownership of your credentials</p>
                                    </div>
                                </div>
                                <div className="flex items-start space-x-3">
                                    <div className="w-8 h-8 bg-orange-500/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                                        <Zap className="w-5 h-5 text-orange-400" />
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-white mb-1">Instant Access</h3>
                                        <p className="text-sm text-gray-300">Access credentials anytime, anywhere</p>
                                    </div>
                                </div>
                            </div>

                            <div className="flex flex-col sm:flex-row gap-4">
                                <Link to="/contact" className="btn-primary inline-flex items-center justify-center">
                                    Get Your Free Wallet
                                    <ArrowRight size={20} className="ml-2" />
                                </Link>
                                <Link to="/features" className="btn-secondary inline-flex items-center justify-center">
                                    Explore Features
                                </Link>
                            </div>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, x: 50 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.6, delay: 0.2 }}
                            className="relative"
                        >
                            <div className="relative">
                                {/* Background Glow Effects */}
                                <div className="absolute inset-0 bg-gradient-to-r from-primary-600/30 to-neon-600/30 rounded-2xl blur-3xl"></div>
                                <div className="absolute -inset-4 bg-gradient-to-r from-neon-400/20 via-primary-500/20 to-neon-400/20 rounded-3xl blur-2xl"></div>
                                
                                <div className="relative glass rounded-2xl p-8 border border-white/20">

                                    {/* Credential Types Preview */}
                                    <div className="space-y-3">
                                        <div className="text-sm font-medium text-gray-300 text-center mb-4">
                                            ✨ Store Any Type of Credential
                                        </div>
                                        
                                        <div className="bg-gradient-to-r from-blue-500/20 to-cyan-500/20 rounded-xl p-3 border border-blue-500/30">
                                            <div className="flex items-center space-x-3">
                                                <div className="w-10 h-10 bg-blue-500/30 rounded-lg flex items-center justify-center">
                                                    <span className="text-lg">🎓</span>
                                                </div>
                                                <div>
                                                    <h4 className="font-medium text-white text-sm">Education Credentials</h4>
                                                    <p className="text-xs text-gray-400">Diplomas • Certificates • Transcripts</p>
                                                </div>
                                            </div>
                                        </div>
                                        
                                        <div className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 rounded-xl p-3 border border-green-500/30">
                                            <div className="flex items-center space-x-3">
                                                <div className="w-10 h-10 bg-green-500/30 rounded-lg flex items-center justify-center">
                                                    <span className="text-lg">🏢</span>
                                                </div>
                                                <div>
                                                    <h4 className="font-medium text-white text-sm">Professional Licenses</h4>
                                                    <p className="text-xs text-gray-400">Medical • Legal • Engineering</p>
                                                </div>
                                            </div>
                                        </div>
                                        
                                        <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-xl p-3 border border-purple-500/30">
                                            <div className="flex items-center space-x-3">
                                                <div className="w-10 h-10 bg-purple-500/30 rounded-lg flex items-center justify-center">
                                                    <span className="text-lg">🆔</span>
                                                </div>
                                                <div>
                                                    <h4 className="font-medium text-white text-sm">Identity Documents</h4>
                                                    <p className="text-xs text-gray-400">ID Cards • Passports • Driver&apos;s License</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Trust Indicators */}
                                    <div className="mt-6 pt-4 border-t border-white/20">
                                        <div className="flex items-center justify-center space-x-4 text-xs text-gray-400">
                                            <div className="flex items-center space-x-1">
                                                <Shield className="w-3 h-3" />
                                                <span>ISO 27001</span>
                                            </div>
                                            <div className="flex items-center space-x-1">
                                                <CheckCircle className="w-3 h-3" />
                                                <span>SOC 2 Type II</span>
                                            </div>
                                            <div className="flex items-center space-x-1">
                                                <span className="text-green-400">🔒</span>
                                                <span>GDPR Compliant</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Wallet Feature Section */}
            <section className="py-20 bg-surface-100">
                <div className="container-custom">
                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        <motion.div
                            initial={{ opacity: 0, x: -50 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.6 }}
                        >
                            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
                                Your verifiable credentials at your fingertips.
                                <span className="text-neon-300"> Always.</span>
                            </h2>
                            <p className="text-lg text-gray-300 mb-8 leading-relaxed">
                                With Documint Identity Wallet, you have full control over your digital identity.
                                Securely store, manage, and share your Verifiable Credentials (VCs)—such as IDs,
                                diplomas, licenses, and more—right from your device.
                            </p>
                            <p className="text-lg text-gray-300 mb-8 leading-relaxed">
                                Powered by decentralized identifiers (DIDs) and blockchain technology, Documint
                                ensures that your credentials are tamper-proof, instantly verifiable, and always
                                under your control.
                            </p>
                            <p className="text-lg text-gray-300 mb-8 leading-relaxed">
                                Whether you&apos;re applying for a job, accessing services, or proving your identity
                                online, your trusted credentials are just a tap away—private, secure, and effortless.
                            </p>
                            <div className="grid md:grid-cols-2 gap-6 mb-8">
                                <div className="flex items-start space-x-3">
                                    <div className="w-8 h-8 bg-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                                        <Wallet className="w-5 h-5 text-blue-400" />
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-white mb-1">Digital Wallet</h3>
                                        <p className="text-sm text-gray-400">All credentials in one secure place</p>
                                    </div>
                                </div>
                                <div className="flex items-start space-x-3">
                                    <div className="w-8 h-8 bg-green-500/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                                        <CheckCircle className="w-5 h-5 text-green-400" />
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-white mb-1">Instant Verification</h3>
                                        <p className="text-sm text-gray-400">Real-time credential validation</p>
                                    </div>
                                </div>
                                <div className="flex items-start space-x-3">
                                    <div className="w-8 h-8 bg-purple-500/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                                        <Shield className="w-5 h-5 text-purple-400" />
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-white mb-1">Tamper-Proof</h3>
                                        <p className="text-sm text-gray-400">Blockchain-secured credentials</p>
                                    </div>
                                </div>
                                <div className="flex items-start space-x-3">
                                    <div className="w-8 h-8 bg-orange-500/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                                        <Zap className="w-5 h-5 text-orange-400" />
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-white mb-1">One Tap Access</h3>
                                        <p className="text-sm text-gray-400">Effortless credential sharing</p>
                                    </div>
                                </div>
                            </div>
                            <Link to="/features" className="btn-primary inline-flex items-center">
                                Explore Wallet Features
                                <ArrowRight size={20} className="ml-2" />
                            </Link>
                        </motion.div>
                        <motion.div
                            initial={{ opacity: 0, x: 50 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.6, delay: 0.2 }}
                            className="relative"
                        >
                            <div className="relative">
                                <div className="absolute inset-0 bg-gradient-to-r from-primary-600/30 to-neon-600/30 rounded-2xl blur-2xl"></div>
                                <div className="glass rounded-2xl p-8 border border-white/20 relative">
                                    <h3 className="text-lg font-semibold text-white mb-6 text-center">Documint Wallet Preview</h3>
                                    <div className="space-y-4">
                                        <div className="bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-xl p-4 border border-white/20">
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center space-x-3">
                                                    <div className="w-10 h-10 bg-blue-500/30 rounded-lg flex items-center justify-center">
                                                        <Wallet className="w-5 h-5 text-blue-400" />
                                                    </div>
                                                    <div>
                                                        <h4 className="font-medium text-white">University Degree</h4>
                                                        <p className="text-xs text-gray-400">Stanford University</p>
                                                    </div>
                                                </div>
                                                <CheckCircle className="w-5 h-5 text-green-400" />
                                            </div>
                                        </div>
                                        <div className="bg-gradient-to-r from-green-500/20 to-blue-500/20 rounded-xl p-4 border border-white/20">
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center space-x-3">
                                                    <div className="w-10 h-10 bg-green-500/30 rounded-lg flex items-center justify-center">
                                                        <Shield className="w-5 h-5 text-green-400" />
                                                    </div>
                                                    <div>
                                                        <h4 className="font-medium text-white">Driver&apos;s License</h4>
                                                        <p className="text-xs text-gray-400">State of California</p>
                                                    </div>
                                                </div>
                                                <CheckCircle className="w-5 h-5 text-green-400" />
                                            </div>
                                        </div>
                                        <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-xl p-4 border border-white/20">
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center space-x-3">
                                                    <div className="w-10 h-10 bg-purple-500/30 rounded-lg flex items-center justify-center">
                                                        <Zap className="w-5 h-5 text-purple-400" />
                                                    </div>
                                                    <div>
                                                        <h4 className="font-medium text-white">Professional License</h4>
                                                        <p className="text-xs text-gray-400">Medical Board</p>
                                                    </div>
                                                </div>
                                                <CheckCircle className="w-5 h-5 text-green-400" />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* QR Code Sharing Section */}
            <section className="py-20 bg-surface-200">
                <div className="container-custom">
                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        <motion.div
                            initial={{ opacity: 0, x: -50 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.6 }}
                            className="relative"
                        >
                            <div className="glass rounded-2xl p-8 border border-white/20">
                                <h3 className="text-lg font-semibold text-white mb-4">Why QR Code Sharing?</h3>
                                <div className="space-y-3">
                                    <div className="flex items-center justify-between">
                                        <span className="text-gray-300">Traditional Method</span>
                                        <span className="text-white font-medium">Documint Way</span>
                                    </div>
                                    <div className="border-t border-white/20"></div>
                                    <div className="flex items-center justify-between">
                                        <span className="text-red-600 text-sm">❌ Print & Sign</span>
                                        <span className="text-green-600 text-sm">✅ Digital QR Scan</span>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <span className="text-red-600 text-sm">❌ Email PDFs</span>
                                        <span className="text-green-600 text-sm">✅ Instant Sharing</span>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <span className="text-red-600 text-sm">❌ Manual Verification</span>
                                        <span className="text-green-600 text-sm">✅ Auto Verification</span>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <span className="text-red-600 text-sm">❌ Days to Process</span>
                                        <span className="text-green-600 text-sm">✅ Seconds to Complete</span>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                        <motion.div
                            initial={{ opacity: 0, x: 50 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.6, delay: 0.2 }}
                        >
                            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
                                Scan to share your credentials.
                                <span className="text-neon-300"> No scans, emails and pdfs.</span>
                            </h2>
                            <p className="text-lg text-gray-300 mb-6 leading-relaxed">
                                Forget the hassle of printing, signing, scanning, and emailing documents. With Documint
                                Identity Wallet, securely share your Verifiable Credentials in seconds—just scan a QR
                                code or tap your device.
                            </p>
                            <p className="text-lg text-gray-300 mb-8 leading-relaxed">
                                Your credentials are digitally signed, instantly verifiable, and privacy-preserving,
                                ensuring that only the necessary information is shared. No more outdated PDFs or manual
                                verification—just fast, secure, and seamless identity sharing anytime, anywhere.
                            </p>
                            <div className="grid md:grid-cols-2 gap-6 mb-8">
                                <div className="flex items-start space-x-3">
                                    <div className="w-8 h-8 bg-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                                        <QrCode className="w-5 h-5 text-blue-400" />
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-white mb-1">QR Code & NFC</h3>
                                        <p className="text-sm text-gray-300">Multiple sharing methods available</p>
                                    </div>
                                </div>
                                <div className="flex items-start space-x-3">
                                    <div className="w-8 h-8 bg-green-500/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                                        <Shield className="w-5 h-5 text-green-400" />
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-white mb-1">Privacy-Preserving</h3>
                                        <p className="text-sm text-gray-300">Share only what&apos;s necessary</p>
                                    </div>
                                </div>
                                <div className="flex items-start space-x-3">
                                    <div className="w-8 h-8 bg-purple-500/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                                        <Zap className="w-5 h-5 text-purple-400" />
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-white mb-1">Instant Verification</h3>
                                        <p className="text-sm text-gray-300">Real-time validation in seconds</p>
                                    </div>
                                </div>
                                <div className="flex items-start space-x-3">
                                    <div className="w-8 h-8 bg-orange-500/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                                        <CheckCircle className="w-5 h-5 text-orange-400" />
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-white mb-1">Tamper-Proof</h3>
                                        <p className="text-sm text-gray-300">Cryptographically secured</p>
                                    </div>
                                </div>
                            </div>
                            <Link to="/contact" className="btn-primary inline-flex items-center">
                                Get Your Wallet
                                <ArrowRight size={20} className="ml-2" />
                            </Link>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Call to Action */}
            <section className="py-20 bg-gradient-to-r from-primary-600 to-neon-600">
                <div className="container-custom text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                    >
                        <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
                            Ready to Control Your Digital Identity?
                        </h2>
                        <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
                            Experience the future of identity management with Documint Wallet. Secure, private, and always in your control.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <a href="https://app.passhub.documint.id/signup" target="_blank" rel="noopener noreferrer" className="bg-white text-primary-600 hover:bg-gray-100 px-8 py-3 rounded-lg font-semibold transition-colors duration-200 inline-flex items-center">
                                Start Free Trial
                                <ArrowRight size={20} className="ml-2" />
                            </a>
                            <Link to="/contact" className="border border-white/20 text-white hover:bg-white/10 px-8 py-3 rounded-lg font-semibold transition-colors duration-200 inline-flex items-center">
                                Contact Sales
                            </Link>
                        </div>
                    </motion.div>
                </div>
            </section>
        </div>
    );
};

export default DocumintWallet;